/**
 * CrewLeadCloseoutDialog (PR4b)
 * ─────────────────────────────
 * Captures actual hours by role at job closeout for INTERNAL CREW jobs.
 *
 * Hard rules (mirrors `recordLaborActual`):
 *   • Append-only. Corrections create a NEW record pointing back via `supersedesId`.
 *   • Computation uses canonical `getLoadedRate()` — never inline.
 *   • Crew Lead attribution is required (`enteredBy`).
 *   • NEVER displays contractValue / price / customer-facing dollars.
 *
 * UX:
 *   • Pre-fills with the latest non-superseded record when re-opening.
 *   • Shows live "actual $" vs "estimate $" delta as the lead edits.
 */

import { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import {
  listLoadedRates,
  getLoadedRate,
  type InternalCrewRole,
} from "@/data/finance/payRun/internalLaborRates";
import {
  recordLaborActual,
  getEffectiveLaborActual,
} from "@/data/finance/payRun/laborActuals";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobLabel: string;
  /** Estimated labor $ for delta display. Optional. */
  estimatedDollars?: number | null;
}

export default function CrewLeadCloseoutDialog({
  open, onOpenChange, jobId, jobLabel, estimatedDollars,
}: Props) {
  const appData = useAppData();
  const laborActualRecords: any[] = (appData as any).laborActualRecords ?? [];
  const setLaborActualRecords = (appData as any).setLaborActualRecords ?? (() => {});
  const { role } = useRole();

  const effective = useMemo(
    () => getEffectiveLaborActual(jobId, laborActualRecords),
    [jobId, laborActualRecords],
  );

  // Hours-per-role keyed input. Seed from latest effective record when present.
  const [hours, setHours] = useState<Record<InternalCrewRole, string>>(() => {
    const seed: Partial<Record<InternalCrewRole, string>> = {};
    if (effective) {
      for (const l of effective.lines) seed[l.role] = String(l.hours);
    }
    return seed as Record<InternalCrewRole, string>;
  });
  const [note, setNote] = useState(effective?.note ?? "");
  const [enteredBy, setEnteredBy] = useState(effective?.enteredBy ?? role ?? "");

  const rates = listLoadedRates();

  const liveTotal = useMemo(() => {
    return rates.reduce((sum, r) => {
      const h = Number(hours[r.role] ?? 0);
      if (!Number.isFinite(h) || h <= 0) return sum;
      return sum + h * r.loadedRate;
    }, 0);
  }, [hours, rates]);

  const variance =
    typeof estimatedDollars === "number" && estimatedDollars > 0
      ? liveTotal - estimatedDollars
      : null;
  const variancePct =
    variance != null && estimatedDollars && estimatedDollars > 0
      ? (variance / estimatedDollars) * 100
      : null;

  const handleSubmit = () => {
    const lines = rates
      .map((r) => ({ role: r.role, hours: Number(hours[r.role] ?? 0) }))
      .filter((l) => Number.isFinite(l.hours) && l.hours > 0);

    const result = recordLaborActual({
      jobId,
      lines,
      enteredBy: enteredBy.trim(),
      supersedesId: effective?.id,
      note: note.trim() || undefined,
    });

    if (result.ok !== true) {
      const failure = result as { ok: false; reason: string };
      const map: Record<string, string> = {
        no_lines: "Enter hours for at least one role.",
        invalid_hours: "Hours must be zero or greater.",
        missing_entered_by: "Crew Lead name is required.",
      };
      toast({
        title: "Couldn't log labor",
        description: map[failure.reason] ?? "Please review the form.",
        variant: "destructive",
      });
      return;
    }

    setLaborActualRecords((prev) => [...prev, result.record]);
    toast({
      title: effective ? "Labor actuals updated" : "Labor actuals logged",
      description: `${jobLabel} · $${result.record.actualDollars.toLocaleString()}`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Crew Lead Closeout — Labor Actuals</DialogTitle>
          <DialogDescription>
            Enter actual hours by role for <span className="font-medium">{jobLabel}</span>.
            Append-only — corrections add a new entry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="enteredBy">Crew Lead</Label>
            <Input
              id="enteredBy"
              value={enteredBy}
              onChange={(e) => setEnteredBy(e.target.value)}
              placeholder="Your name"
              className="mt-1"
            />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Hours by role</div>
            <div className="rounded-lg border divide-y">
              {rates.map((r) => {
                const h = Number(hours[r.role] ?? 0);
                const dollars = Number.isFinite(h) && h > 0 ? h * r.loadedRate : 0;
                return (
                  <div key={r.role} className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{r.label}</div>
                      <div className="text-[11px] text-muted-foreground">
                        ${r.loadedRate}/hr loaded
                      </div>
                    </div>
                    <Input
                      type="number"
                      min={0}
                      step={0.25}
                      value={hours[r.role] ?? ""}
                      onChange={(e) =>
                        setHours((prev) => ({ ...prev, [r.role]: e.target.value }))
                      }
                      className="w-24 text-right"
                      placeholder="0"
                    />
                    <div className="w-24 text-right text-sm tabular-nums">
                      {dollars > 0 ? `$${dollars.toLocaleString()}` : "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border bg-muted/30 px-3 py-2 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Actual labor</span>
              <span className="font-semibold tabular-nums">
                ${liveTotal.toLocaleString()}
              </span>
            </div>
            {typeof estimatedDollars === "number" && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Estimated</span>
                <span className="tabular-nums">${estimatedDollars.toLocaleString()}</span>
              </div>
            )}
            {variance != null && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Variance</span>
                <Badge
                  variant={Math.abs(variancePct ?? 0) > 10 ? "destructive" : "secondary"}
                  className="font-mono"
                >
                  {variance >= 0 ? "+" : ""}${variance.toLocaleString()}
                  {variancePct != null && ` (${variancePct >= 0 ? "+" : ""}${variancePct.toFixed(0)}%)`}
                </Badge>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="note">Note (optional)</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything notable about the day — overruns, rework, etc."
              className="mt-1"
              rows={2}
            />
          </div>

          {effective && (
            <div className="text-[11px] text-muted-foreground">
              Latest entry by {effective.enteredBy} on{" "}
              {new Date(effective.enteredAt).toLocaleString()}. Submitting will append a new
              record superseding it.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit}>
            {effective ? "Submit correction" : "Log actuals"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
